import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatDate';

const StaffingCommentsPage = () => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [clients, setClients] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    client_id: '',
    requirement_id: '',
    entity_type: '',
    search: ''
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get('/staffing/clients', { params: { limit: 1000 } });
        const list = response.data.data?.clients || [];
        setClients(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load clients:', error);
        setClients([]);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        setRequirements([]);
        const params = { limit: 1000 };
        if (filters.client_id) params.client_id = filters.client_id;
        const response = await api.get('/staffing/requirements', { params });
        const list = response.data.data?.requirements || [];
        setRequirements(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load requirements:', error);
        setRequirements([]);
      }
    };

    fetchRequirements();
  }, [filters.client_id]);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pageSize,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value))
        };
        const response = await api.get('/staffing/comments', { params });
        const data = response.data.data || {};
        setComments(Array.isArray(data.comments) ? data.comments : []);
        setPagination(data.pagination || null);
      } catch (error) {
        console.error('Failed to load comments:', error);
        setComments([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [page, pageSize, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'client_id') {
        next.requirement_id = '';
      }
      return next;
    });
    setPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Comments</h1>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Client</label>
                <select
                  name="client_id"
                  className="form-select"
                  value={filters.client_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All clients</option>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Requirement</label>
                <select
                  name="requirement_id"
                  className="form-select"
                  value={filters.requirement_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All requirements</option>
                  {requirements.map((requirement) => (
                    <option key={requirement._id} value={requirement._id}>
                      {requirement.title}
                      {requirement.client_id?.name ? ` (${requirement.client_id.name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">On</label>
                <select
                  name="entity_type"
                  className="form-select"
                  value={filters.entity_type}
                  onChange={handleFilterChange}
                >
                  <option value="">Requirements and candidates</option>
                  <option value="staffing_requirement">Requirements</option>
                  <option value="staffing_candidate">Candidates</option>
                </select>
              </div>
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search comment text"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table table-constrained mb-0 table-hover">
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead className="table-light border-light">
                  <tr>
                    <th>S.No.</th>
                    <th>Comment</th>
                    <th>On</th>
                    <th>Name</th>
                    <th>Client</th>
                    <th>Requirement</th>
                    <th>By</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        No comments found
                      </td>
                    </tr>
                  ) : (
                    comments.map((comment, index) => (
                      <tr key={comment._id} className="align-middle">
                        <td className="text-nowrap">{(page - 1) * pageSize + index + 1}</td>
                        <td className="cell-wrap">{comment.comment}</td>
                        <td>{comment.context?.type || 'N/A'}</td>
                        <td className="cell-wrap">{comment.context?.name || 'N/A'}</td>
                        <td className="cell-wrap">{comment.context?.client_name || 'N/A'}</td>
                        <td className="cell-wrap">{comment.context?.requirement_title || 'N/A'}</td>
                        <td className="cell-wrap">{comment.commented_by?.name || 'N/A'}</td>
                        <td className="cell-wrap">{formatDateTime(comment.created_at)}</td>
                        <td>
                          {comment.context?.link && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(comment.context.link)}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination
                  pagination={pagination}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StaffingCommentsPage;
